import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { toast } from "react-toastify";
import { API_ciudadanos } from "../config/apiConfig";
import { useAuth } from "./AuthContext";

const NotificacionesContext = createContext();

export const useNotificaciones = () => {
  const context = useContext(NotificacionesContext);
  if (!context) {
    throw new Error(
      "useNotificaciones debe usarse dentro de NotificacionesProvider"
    );
  }
  return context;
};

export const NotificacionesProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [hayPendientesExternos, setHayPendientesExternos] = useState(false);
  const [hayCitasPendientes, setHayCitasPendientes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);

  // Cache y control de llamadas
  const cacheRef = useRef({
    data: null,
    timestamp: null,
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutos
  });

  const fetchingRef = useRef(false);

  const fetchNotificaciones = async (forceRefresh = false) => {
    // Verificar cache si no es refresh forzado
    if (!forceRefresh) {
      const now = Date.now();
      const cache = cacheRef.current;

      if (
        cache.data &&
        cache.timestamp &&
        now - cache.timestamp < cache.CACHE_DURATION
      ) {
        setHayPendientesExternos(cache.data.hayPendientesExternos);
        setHayCitasPendientes(cache.data.hayCitasPendientes);
        return;
      }
    }

    // Evitar llamadas múltiples simultáneas
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setIsLoading(true);

    try {
      const res = await fetch(API_ciudadanos, {
        headers: {
          "x-auth-token": token,
        },
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setNotificaciones(data);
      }

      const pendientes = data.filter((u) => u.estadoUsuario === "Pendiente");
      const hayPendientesExternos = pendientes.length > 0;

      // Optimización: Solo verificar citas de los primeros 10 usuarios
      const ciudadanosLimitados = data.slice(0, 10);

      // Hacer las llamadas en paralelo
      const promesasCitas = ciudadanosLimitados.map(async (ciudadano) => {
        try {
          const resCitas = await fetch(
            `${API_ciudadanos}/${ciudadano.userId}/citas`
          );
          const citasUsuario = await resCitas.json();
          const pendientesUsuario = citasUsuario.filter(
            (c) => c.state === "Pendiente"
          );
          return pendientesUsuario.length;
        } catch (error) {
          console.error(
            `Error obteniendo citas para usuario ${ciudadano.userId}:`,
            error
          );
          return 0;
        }
      });

      const resultados = await Promise.all(promesasCitas);
      const totalCitasPendientes = resultados.reduce(
        (sum, count) => sum + count,
        0
      );
      const hayCitasPendientes = totalCitasPendientes > 0;

      // Guardar en cache
      const now = Date.now();
      cacheRef.current = {
        data: { hayPendientesExternos, hayCitasPendientes },
        timestamp: now,
      };

      setHayPendientesExternos(hayPendientesExternos);
      setHayCitasPendientes(hayCitasPendientes);
    } catch (err) {
      console.error("Error cargando notificaciones:", err);
      toast.error("Error al cargar las notificaciones");
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchNotificaciones();
    }
  }, [isAuthenticated, token]);

  const value = {
    hayPendientesExternos,
    hayCitasPendientes,
    isLoading,
    refetchNotificaciones: () => fetchNotificaciones(true),
  };

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
    </NotificacionesContext.Provider>
  );
};
